// Shared assigned-track completeness: dots + "X of Y in" + legend.
// Used by the Agenda card and Grid tiles. The denominator is always the
// project's captured assigned tracks - never a fixed seven. QUIET is a
// deliberate "nothing this week" response and counts as in; it is not missing.

import React from "react";
import { T } from "../../../app/tokens.js";
import { TRACK_STATUS_META, trackLabel, assignedTrackCompleteness } from "./weeklyModel.js";

export const TRACK_STATE_META = Object.freeze({
  reported: { label: "Reported" },
  quiet: { label: "Nothing this week" },
  not_yet: { label: "Not yet reported" },
});

export function trackDotDescription(row) {
  const label = trackLabel(row.trackKey);
  if (row.state === "quiet") return `${label}: nothing this week (quiet)${row.response?.authorName ? ` - marked by ${row.response.authorName}` : ""}`;
  if (row.state === "reported") return `${label}: reported${row.status ? ` - ${TRACK_STATUS_META[row.status]?.label || row.status}` : " - no status set"}`;
  return `${label}: not yet reported`;
}

export function TrackDot({ row, size = 9, testId, focusable = false }) {
  const desc = trackDotDescription(row);
  const statusColor = row.status ? TRACK_STATUS_META[row.status]?.color : null;
  const style = row.state === "reported"
    ? { background: statusColor || T.blue }
    : row.state === "quiet"
      ? { background: "transparent", boxShadow: `inset 0 0 0 2px ${T.mid}` }
      : { background: T.border, boxShadow: `inset 0 0 0 1px ${T.borderDark}` };
  return (
    <span
      role="img"
      aria-label={desc}
      title={desc}
      tabIndex={focusable ? 0 : undefined}
      className={focusable ? "wk-focus" : undefined}
      data-testid={testId}
      data-state={row.state}
      style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", flexShrink: 0, ...style }}
    />
  );
}

/** Compact dots + count. `surface` keeps testids distinct between Agenda and Grid. */
export function TrackCompleteness({ project, surface, showLabels = false }) {
  const c = assignedTrackCompleteness(project);
  const base = `weekly-completeness-${surface}-${project.id}`;
  if (!c.available) {
    return (
      <span data-testid={base} data-available="false" aria-label="Track statuses" role="group" style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
        Assignments not captured - completeness unavailable
      </span>
    );
  }
  if (c.total === 0) {
    return (
      <span data-testid={base} data-available="true" aria-label="Track statuses" role="group" style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
        No tracks assigned
      </span>
    );
  }
  const summary = `${c.inCount} of ${c.total} in${c.quietCount ? ` - ${c.quietCount} quiet` : ""}`;
  return (
    <span data-testid={base} data-available="true" role="group" aria-label="Track statuses" style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", gap: showLabels ? 5 : 4, flexWrap: "wrap" }}>
        {c.tracks.map((row) => showLabels ? (
          <span key={row.trackKey} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: T.fsXxs, color: T.mid, background: T.lightGray, borderRadius: 6, padding: "2px 6px" }}>
            <TrackDot row={row} size={7} focusable testId={`${base}-${row.trackKey}`} />{trackLabel(row.trackKey)}
          </span>
        ) : (
          <TrackDot key={row.trackKey} row={row} focusable testId={`${base}-${row.trackKey}`} />
        ))}
      </span>
      <span data-testid={`${base}-count`} style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: c.inCount === c.total ? T.ragGreenText : T.dark, whiteSpace: "nowrap" }}>{summary}</span>
    </span>
  );
}

export function TrackLegend({ testId = "weekly-track-legend" }) {
  const items = [
    ["on_track", { background: TRACK_STATUS_META.on_track.color }, "Green - reported, On track"],
    ["watch", { background: TRACK_STATUS_META.watch.color }, "Amber - reported, Watch"],
    ["blocked", { background: TRACK_STATUS_META.blocked.color }, "Red - reported, Blocked"],
    ["norag", { background: T.blue }, "Blue - reported, no status set (not green)"],
    ["quiet", { boxShadow: `inset 0 0 0 2px ${T.mid}` }, "Ring - nothing this week, counts as in"],
    ["not_yet", { background: T.border, boxShadow: `inset 0 0 0 1px ${T.borderDark}` }, "Grey - not yet reported"],
  ];
  return (
    <div data-testid={testId} aria-label="Track dot legend" role="note" style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: T.fsXxs, color: T.mid }}>
      {items.map(([k, style, text]) => (
        <span key={k} data-testid={`${testId}-${k}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span aria-hidden="true" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", ...style }} />{text}
        </span>
      ))}
      <span>Dots cover assigned tracks only</span>
    </div>
  );
}
